import React, { useState, useEffect } from 'react';
import {
  Form,
  Input,
  Button,
  Select,
  Space,
  Row,
  Col,
  notification,
  Spin,
  Upload,
  Image
} from 'antd';
import { UploadOutlined, CheckCircleOutlined, InfoCircleOutlined, PictureOutlined } from '@ant-design/icons';
import { API_BASE_URL } from '../../config';

const { Option } = Select;

const EditAdditionalInformationStudent = ({ studentId, onClose }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [religionOptions, setReligionOptions] = useState([]);
  const [existingFile, setExistingFile] = useState(null);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState('');

  useEffect(() => {
    fetchReligionEnum();
    if (studentId) {
      fetchStudentData();
    }
  }, [studentId]);

  const fetchReligionEnum = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/Enum/Religion`);
      if (!response.ok) {
        throw new Error('Failed to fetch religion options');
      }
      const data = await response.json();
      const religionData = data.Religion;
      
      const options = Object.entries(religionData).map(([value, label]) => ({
        value: parseInt(value),
        label: label
      }));
      
      setReligionOptions(options);
    } catch (error) {
      notification.error({
        message: 'Error',
        description: 'Failed to load religion options',
        placement: 'topRight',
        icon: <InfoCircleOutlined style={{ color: '#ff4d4f' }} />,
        style: {
          borderLeft: '4px solid #ff4d4f'
        }
      });
    }
  };

  const fetchStudentData = async () => {
    try {
      setLoading(true);
      
      const storedUser = localStorage.getItem('userInfo');
      if (!storedUser) {
        throw new Error('No user data found');
      }

      const { token } = JSON.parse(storedUser);

      const response = await fetch(`${API_BASE_URL}/api/Student/find/${studentId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch student data');
      }

      const data = await response.json();
      console.log('data',data);
      form.setFieldsValue({
        primaryLanguage: data.primaryLanguage || '',
        religion: data.religion,
        otherLanguages: data.otherLanguages || '',
      });

      // Handle existing birth certificate image
      if (data.birthCertificateFile) {
        const fileData = {
          uid: '-1',
          name: 'Birth Certificate',
          status: 'done',
          url: `data:image/jpeg;base64,${data.birthCertificateFile}`,
          thumbUrl: `data:image/jpeg;base64,${data.birthCertificateFile}`,
        };
        setExistingFile(fileData);
        setFileList([fileData]);
      }
    } catch (error) {
      notification.error({
        message: 'Error',
        description: error.message || 'Failed to load student information',
        placement: 'topRight',
        icon: <InfoCircleOutlined style={{ color: '#ff4d4f' }} />,
        style: {
          borderLeft: '4px solid #ff4d4f'
        }
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      
      const storedUser = localStorage.getItem('userInfo');
      if (!storedUser) {
        throw new Error('No user data found');
      }

      const { token } = JSON.parse(storedUser);

      // Handle birth certificate file
      let birthCertificateBase64 = null;
      if (fileList.length > 0) {
        const file = fileList[0];
        if (file.originFileObj) {
          const reader = new FileReader();
          birthCertificateBase64 = await new Promise((resolve, reject) => {
            reader.onload = () => resolve(reader.result.split(',')[1]);
            reader.onerror = reject;
            reader.readAsDataURL(file.originFileObj);
          });
        } else if (existingFile && file.uid === existingFile.uid) {
          birthCertificateBase64 = file.url.split(',')[1];
        }
      }

      const requestData = {
        primaryLanguage: values.primaryLanguage,
        religion: parseInt(values.religion),
        otherLanguages: values.otherLanguages,
        birthCertificateFile: birthCertificateBase64
      };
      console.log('requestData',requestData);
      const response = await fetch(`${API_BASE_URL}/api/Student/Update/${studentId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to update student information');
      }

      notification.success({
        message: 'Update Successful',
        description: 'Student information has been updated successfully',
        placement: 'topRight',
        duration: 3,
        icon: <CheckCircleOutlined style={{ color: '#52c41a' }} />,
        style: {
          borderLeft: '4px solid #52c41a'
        }
      });
      onClose();
    } catch (error) {
      notification.error({
        message: 'Update Failed',
        description: error.message || 'Failed to update student information',
        placement: 'topRight',
        duration: 4,
        icon: <InfoCircleOutlined style={{ color: '#ff4d4f' }} />,
        style: {
          borderLeft: '4px solid #ff4d4f'
        }
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = ({ fileList }) => {
    setFileList(fileList);
  };

  const handlePreview = async (file) => {
    if (file.url) {
      setPreviewImage(file.url);
      setPreviewVisible(true);
    }
  };

  const uploadProps = {
    beforeUpload: (file) => {
      const isImage = file.type.startsWith('image/');
      if (!isImage) {
        notification.error({
          message: 'Invalid File',
          description: 'You can only upload image files!',
          placement: 'topRight'
        });
      }
      return false;
    },
    accept: 'image/*',
    fileList,
    onChange: handleFileChange,
    onPreview: handlePreview,
    maxCount: 1,
    listType: 'picture-card',
    showUploadList: {
      showPreviewIcon: true,
      showRemoveIcon: true
    }
  };

  return (
    <Spin spinning={loading}>
      <Form
        form={form}
        onFinish={handleSubmit}
        layout="vertical"
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="primaryLanguage"
              label="Primary Language"
              rules={[{ required: true, message: 'Please input primary language!' }]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="religion"
              label="Religion"
              rules={[{ required: true, message: 'Please select religion!' }]}
            >
              <Select>
                {religionOptions.map(option => (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              name="otherLanguages"
              label="Other Languages"
            >
              <Input.TextArea rows={4} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              name="birthCertificateFile"
              label="Birth Certificate"
              extra="Upload an image of the birth certificate"
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <Upload {...uploadProps}>
                  {fileList.length === 0 && (
                    <div>
                      <PictureOutlined />
                      <div style={{ marginTop: 8 }}>Upload</div>
                    </div>
                  )}
                </Upload>
                {fileList.length > 0 && (
                  <Button 
                    icon={<UploadOutlined />}
                    onClick={() => {
                      const input = document.createElement('input');
                      input.type = 'file';
                      input.accept = 'image/*';
                      input.onchange = (e) => {
                        const file = e.target.files[0];
                        if (file) {
                          const isImage = file.type.startsWith('image/');
                          if (!isImage) {
                            notification.error({
                              message: 'Invalid File',
                              description: 'You can only upload image files!',
                              placement: 'topRight'
                            });
                            return;
                          }
                          setFileList([{
                            uid: '-2',
                            name: file.name,
                            status: 'done',
                            originFileObj: file,
                          }]);
                        }
                      };
                      input.click();
                    }}
                  >
                    Change Birth Certificate
                  </Button>
                )}
              </div>
            </Form.Item>
          </Col>
        </Row>

        <Row justify="end">
          <Space>
            <Button onClick={onClose}>
              Cancel
            </Button>
            <Button type="primary" htmlType="submit" loading={loading}>
              Save Changes
            </Button>
          </Space>
        </Row>
      </Form>

      {/* Image Preview Modal */}
      {previewVisible && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.65)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
          }}
          onClick={() => setPreviewVisible(false)}
        >
          <Image
            style={{ maxWidth: '90%', maxHeight: '90%' }}
            src={previewImage}
            preview={false}
          />
        </div>
      )}
    </Spin>
  );
};

export default EditAdditionalInformationStudent;
